import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, AlertTriangle } from "lucide-react";
import { BANK_LIST, isKnownBank, normalizeCompe } from "@/data/banks";

/**
 * Seletor de banco — fonte ÚNICA da entrada de `bank_code` na UI.
 *
 * Extraído das três cópias idênticas que viviam em `EmployeeProfile`,
 * `EmployeeFormDialog` e `AdminCredores`. A entrada canônica é o código COMPE de
 * 3 dígitos da `BANK_LIST` (owner 2026-08-03) — o ISPB de 8 dígitos não é
 * digitável em lugar nenhum.
 *
 * Valor que a `BANK_LIST` não conhece (ISPB gravado antes desta regra, ou COMPE
 * criado depois do snapshot do BCB) aparece em destaque no gatilho. Antes, o
 * combobox caía no placeholder "Selecione o banco...": o dado ficava INVISÍVEL
 * para quem edita e era re-gravado intacto no save, porque o form só manda o que
 * já tinha.
 *
 * Aqui a régua é `isKnownBank` (o que EU sei renderizar), NÃO o gate de save
 * `isCompeFormat` — por isso a copy é "banco não reconhecido", não "inválido":
 * um COMPE fora do snapshot é aceito pelo servidor e não pode receber uma
 * mensagem falsa de erro.
 */
const UNKNOWN_BANK_HINT = "Banco não reconhecido — selecione um da lista";

export default function BankCombobox({
  value,
  onChange,
  disabled = false,
  testId,
  placeholder = "Selecione o banco...",
}) {
  const [open, setOpen] = useState(false);
  // Normalização ÚNICA: sem ela " 341" não casa aqui, mas casa no save.
  const code = normalizeCompe(value);
  const selected = BANK_LIST.find((b) => b.code === code);
  const unknown = !!code && !isKnownBank(code);

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} aria-haspopup="listbox"
          disabled={disabled} data-testid={testId}
          title={unknown ? UNKNOWN_BANK_HINT : undefined}
          aria-label={unknown ? `${code} — ${UNKNOWN_BANK_HINT}` : undefined}
          className={`w-full h-9 justify-between bg-black/20 text-left font-normal hover:bg-black/30 ${unknown ? "border-amber-500/60" : "border-white/10"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
          {selected ? (
            <span className="truncate">{selected.code} - {selected.name}</span>
          ) : unknown ? (
            <span className="flex min-w-0 items-center gap-1.5 text-amber-400" data-testid={testId ? `${testId}-unknown` : undefined}>
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{code} — banco não reconhecido</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Pesquisar banco por nome ou código..." />
          <CommandList>
            <CommandEmpty>Nenhum banco encontrado.</CommandEmpty>
            <CommandGroup>
              {BANK_LIST.map((bank) => (
                <CommandItem key={bank.code} value={`${bank.code} ${bank.name}`}
                  onSelect={() => { onChange(bank.code); setOpen(false); }}>
                  <Check className={`mr-2 h-3 w-3 ${code === bank.code ? "opacity-100" : "opacity-0"}`} aria-hidden="true" />
                  <span className="font-mono text-xs mr-2 text-muted-foreground">{bank.code}</span>
                  <span className="truncate text-sm">{bank.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
